import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Bot, Pause, Play, Snowflake, Pencil, Check, X, CircleCheck } from "lucide-react";
import { PageHeader, Panel } from "@/components/dashboard/primitives";
import { StatusBadge, RiskBadge, fmtUSD } from "@/components/common";
import { Button } from "@/components/ui/button";
import Loading from "@/components/dashboard/Loading";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useDashboard, useDashboardActions } from "@/hooks/useDashboard";
import { toast } from "sonner";

function PermissionRow({ label, value }) {
  const allowed = value === "Allowed";
  const blocked = value === "Blocked";
  const color = allowed ? "text-emerald-400" : blocked ? "text-red-400" : "text-amber-400";
  const Icon = allowed ? CircleCheck : blocked ? X : Check;
  return (
    <div className="flex items-center justify-between py-3 border-b border-white/6 last:border-0" data-testid={`permission-${label.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}>
      <span className="text-sm text-slate-300">{label}</span>
      <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${color}`}>
        <Icon className="h-4 w-4" /> {value}
      </span>
    </div>
  );
}

export default function AgentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useDashboard();
  const { setAgentStatus, setWalletStatus } = useDashboardActions();

  if (isLoading || !data) {
    return (<div><PageHeader title="Agent" showDemo={false} /><Loading /></div>);
  }

  const base = data.agents.find((a) => a.id === id) || data.agents[0];
  const wallet = data.wallets.find((w) => w.agent === base.name);
  const remaining = base.monthlyBudget - base.spent;

  const togglePause = async () => {
    const next = base.status === "Active" ? "Paused" : "Active";
    await setAgentStatus(base.id, next);
    toast.success(`${base.name} ${next === "Active" ? "resumed" : "paused"}`);
  };

  const freeze = async () => {
    if (wallet) await setWalletStatus(wallet.id, "Frozen");
    toast.success(`${base.name}'s wallet frozen`, { description: "All payments disabled until unfrozen." });
  };

  return (
    <div>
      <button onClick={() => navigate("/app/agents")} className="mb-5 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white" data-testid="back-to-agents">
        <ArrowLeft className="h-4 w-4" /> Back to agents
      </button>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-7">
        <div className="flex items-center gap-4">
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#4F8CFF]/12 text-[#4F8CFF]">
            <Bot className="h-7 w-7" />
          </span>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-white">{base.name}</h1>
            <div className="flex items-center gap-2 mt-1.5">
              <StatusBadge status={base.status} />
              <RiskBadge risk={base.risk} />
              {wallet?.status === "Frozen" && <StatusBadge status="Frozen" />}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={togglePause} variant="outline" className="bg-transparent border-white/12 text-white hover:bg-white/5 rounded-full gap-1.5" data-testid="pause-agent-btn">
            {base.status === "Active" ? <><Pause className="h-4 w-4" /> Pause Agent</> : <><Play className="h-4 w-4" /> Resume Agent</>}
          </Button>
          <Button onClick={() => toast("Policy editor coming soon")} variant="outline" className="bg-transparent border-white/12 text-white hover:bg-white/5 rounded-full gap-1.5" data-testid="edit-policy-btn">
            <Pencil className="h-4 w-4" /> Edit Policy
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button className="bg-red-500/10 text-red-400 border border-red-500/25 hover:bg-red-500/20 rounded-full gap-1.5" data-testid="freeze-wallet-btn" disabled={wallet?.status === "Frozen"}>
                <Snowflake className="h-4 w-4" /> {wallet?.status === "Frozen" ? "Wallet Frozen" : "Freeze Wallet"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-[#0B1526] border-white/10 text-white">
              <AlertDialogHeader>
                <AlertDialogTitle>Freeze {base.name}'s wallet?</AlertDialogTitle>
                <AlertDialogDescription className="text-slate-400">
                  This immediately disables all payments for this agent. You can unfreeze later from the Wallets page.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-transparent border-white/15 text-white hover:bg-white/5" data-testid="freeze-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={freeze} className="bg-red-500 hover:bg-red-600 text-white" data-testid="freeze-confirm">Freeze wallet</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Wallet Balance", value: fmtUSD(base.walletBalance) },
          { label: "Monthly Budget", value: fmtUSD(base.monthlyBudget) },
          { label: "Spent", value: fmtUSD(base.spent) },
          { label: "Remaining", value: fmtUSD(remaining), accent: "text-emerald-400" },
        ].map((s) => (
          <Panel key={s.label} className="p-5">
            <p className="text-xs text-slate-400">{s.label}</p>
            <p className={`mt-2 text-xl font-semibold ${s.accent || "text-white"}`}>{s.value}</p>
          </Panel>
        ))}
      </div>

      <div className="mt-5 grid lg:grid-cols-2 gap-5">
        <Panel className="p-6" data-testid="permissions-matrix">
          <h3 className="text-base font-semibold text-white mb-2">Permissions</h3>
          <div>
            {Object.entries(base.permissions).map(([k, v]) => (<PermissionRow key={k} label={k} value={v} />))}
            <PermissionRow label="Daily Limit" value={fmtUSD(base.dailyLimit)} />
            <PermissionRow label="Monthly Limit" value={fmtUSD(base.monthlyBudget)} />
          </div>
        </Panel>

        <Panel className="p-6">
          <h3 className="text-base font-semibold text-white mb-5">Budget utilisation</h3>
          <div className="flex items-center justify-between text-sm text-slate-400">
            <span>Spent <span className="text-white">{fmtUSD(base.spent)}</span></span>
            <span>of {fmtUSD(base.monthlyBudget)}</span>
          </div>
          <div className="mt-3 h-3 rounded-full bg-white/8 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-[#4F8CFF] to-[#7C5CFF]" style={{ width: `${Math.min((base.spent / base.monthlyBudget) * 100, 100)}%` }} />
          </div>
          <p className="mt-3 text-xs text-slate-500">{((base.spent / base.monthlyBudget) * 100).toFixed(1)}% of the monthly budget used.</p>
          <div className="mt-6 rounded-xl border border-white/8 bg-white/[0.02] p-4">
            <p className="text-xs font-mono uppercase tracking-[0.18em] text-slate-500">Permission level</p>
            <p className="mt-1.5 text-sm text-white">{base.permission} — transactions over {fmtUSD(base.transactionLimit)} require human approval.</p>
          </div>
        </Panel>
      </div>
    </div>
  );
}
